import { Entity } from '@inter-ikea-core/core';
import { ProductDesign, ProductType } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getProductEntities } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { showInfoMessage } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ChangeColor, { ColorPickerType } from './ChangeColor';
import { getMajorityColor, getReplaceableProduct } from 'rangeConfig/shared/utils/colorUtils/utils';
import White from './icons/base-white.jpg';
import WhiteFrame from './icons/base-white-frame.jpg';
import Oak from './icons/base-oak-effect.jpg';
import DarkGrey from './icons/base-dark-grey.jpg';

const ChangeCabinetStyle = () => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const productEntities = useSelector(getProductEntities);
  const products = useSelector(getMarketProducts);
  const [defaultColor, setDefaultColor] = useState<ProductDesign | null>(null);
  const [frameEntities, setFrameEntities] = useState<Entity[]>([]);

  const baseCabinetProductTypes = useMemo(
    () => [
      ProductType.KNOXHULT_CABINET_ASM,
      ProductType.KNOXHULT_CORNER_CABINET_ASM,
      ProductType.KNOXHULT_SINK_CABINET_ASM,
      ProductType.KNOXHULT_SINK_AND_OVEN_CABINET_ASM,
      ProductType.KNOXHULT_NICHE_CABINET_ASM
    ],
    []
  );

  const colorArray = useMemo(
    () =>
      [
        {
          productDesign: ProductDesign.WHITE,
          image: White
        },
        {
          productDesign: ProductDesign.WHITE_FRAME,
          image: WhiteFrame
        },
        {
          productDesign: ProductDesign.OAK,
          image: Oak
        },
        {
          productDesign: ProductDesign.DARK_GREY,
          image: DarkGrey
        }
      ].filter(color =>
        products.some(
          product =>
            baseCabinetProductTypes.includes(product.type) && product.design === color.productDesign
        )
      ),
    [baseCabinetProductTypes, products]
  );

  useEffect(() => {
    const framesInScene = coreApi.entities.getByProductTypes(baseCabinetProductTypes);
    setFrameEntities(framesInScene);
  }, [baseCabinetProductTypes, coreApi, productEntities]);

  const onChangeColor = (design: ProductDesign) => {
    const results = frameEntities.map(frame => {
      const newFrame = getReplaceableProduct(frame, design, products);
      if (newFrame) {
        return coreApi.products.replace(frame.id, newFrame.coreProductId);
      }
      return null;
    });
    if (results.some(result => result === null || !result.replacedEntityResult.valid)) {
      dispatch(showInfoMessage({ message: 'storageone.changeColorSomeMissingFramesMessage' }));
    }
  };

  useEffect(() => {
    const baseCabinetsInScene = coreApi.entities.getByProductTypes(baseCabinetProductTypes);
    const cabinetsToMatch =
      baseCabinetsInScene.length > 0
        ? baseCabinetsInScene
        : coreApi.entities.getByProductTypes([
            ProductType.KNOXHULT_HIGH_CABINET_ASM,
            ProductType.KNOXHULT_WALL_CABINET_ASM
          ]);
    const designsInGroup = colorArray.map(color => color.productDesign);
    const color = getMajorityColor(cabinetsToMatch, designsInGroup) || colorArray[0]?.productDesign;
    setDefaultColor(color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    colorArray.length > 1 &&
    defaultColor && (
      <ChangeColor
        entities={frameEntities}
        onChangeColor={onChangeColor}
        defaultColor={defaultColor}
        allColors={colorArray}
        colorPickerType={ColorPickerType.IMAGE}
      />
    )
  );
};

export default ChangeCabinetStyle;
